// Ubicaciones (bins) — Inventario → Bodegas → Ubicaciones.
// Fase 1b WMS: define el esquema de niveles (parametrizable) y genera los bins de
// cada bodega por rango (A-D, 1-10) o lista (A,B,C). Código del bin = valores por
// nivel unidos con '-'. El stock vive por SKU × bin (ver operaciones en fases sig.).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Loader2, MapPin, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, fieldCls, inv, invRpc } from './shared';

const TIPOS_BIN = ['almacenamiento', 'picking', 'recepcion', 'despacho', 'cuarentena', 'devolucion'];
const NIVELES_SUGERIDOS = [
  { nombre: 'Zona', abreviatura: 'Z' }, { nombre: 'Pasillo', abreviatura: 'P' },
  { nombre: 'Percha', abreviatura: 'PE' }, { nombre: 'Nivel', abreviatura: 'N' }, { nombre: 'Fila', abreviatura: 'F' },
];

interface Nivel { id: string; orden: number; nombre: string; abreviatura: string | null; es_activo: boolean }
interface Bodega { id: string; nombre: string; codigo: string }
interface Bin { id: string; bodega_id: string; codigo: string; nombre: string | null; tipo: string; estado: string; es_default: boolean; es_activa: boolean }

function cartesian<T>(lists: T[][]): T[][] { return lists.reduce<T[][]>((acc, l) => acc.flatMap((a) => l.map((x) => [...a, x])), [[]]); }

// "A-D"/"1-10"/"01-10" → rango; "A,B,C" → lista
function expandir(modo: 'rango' | 'lista', texto: string): string[] {
  const t = texto.trim(); if (!t) return [];
  if (modo === 'lista') return t.split(',').map((s) => s.trim()).filter(Boolean);
  const m = t.match(/^\s*([A-Za-z0-9]+)\s*-\s*([A-Za-z0-9]+)\s*$/);
  if (!m) return t.split(',').map((s) => s.trim()).filter(Boolean);
  const [a, b] = [m[1], m[2]];
  if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
    const pad = Math.max(a.length, b.length); const lo = parseInt(a, 10), hi = parseInt(b, 10); const out: string[] = [];
    for (let i = lo; i <= hi && out.length < 500; i++) out.push(String(i).padStart(pad, '0'));
    return out;
  }
  if (/^[A-Za-z]$/.test(a) && /^[A-Za-z]$/.test(b)) {
    const lo = a.toUpperCase().charCodeAt(0), hi = b.toUpperCase().charCodeAt(0); const out: string[] = [];
    for (let i = lo; i <= hi; i++) out.push(String.fromCharCode(i));
    return out;
  }
  return [a, b];
}

export function Ubicaciones() {
  const qc = useQueryClient();
  const [vista, setVista] = useState<'bins' | 'niveles'>('bins');
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <MapPin className="w-7 h-7 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">Ubicaciones de bodega</h1>
          <p className="text-sm text-muted-foreground">Bins con esquema de niveles parametrizable · stock por ubicación</p>
        </div>
      </div>
      <div className="flex gap-1 border-b">
        {([['bins', 'Ubicaciones por bodega'], ['niveles', 'Esquema de niveles']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setVista(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${vista === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{l}</button>
        ))}
      </div>
      {vista === 'niveles' ? <TabNiveles qc={qc} /> : <TabBins qc={qc} />}
    </div>
  );
}

// ── Esquema de niveles ────────────────────────────────────────────────────────
function TabNiveles({ qc }: { qc: ReturnType<typeof useQueryClient> }) {
  const dlg = useDialogoEnLinea();
  const [form, setForm] = useState({ nombre: '', abreviatura: '', orden: 0 });
  const { data: niveles = [], isFetching } = useQuery<Nivel[]>({ queryKey: ['ubic-niveles'], queryFn: () => inv.list<Nivel>('ubicacion_niveles', '?order=orden') });
  const refetch = () => qc.invalidateQueries({ queryKey: ['ubic-niveles'] });
  const agregar = useMutation({
    mutationFn: () => inv.create('ubicacion_niveles', { nombre: form.nombre, abreviatura: form.abreviatura || null, orden: form.orden || niveles.length + 1 }),
    onSuccess: () => { setForm({ nombre: '', abreviatura: '', orden: 0 }); void refetch(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const actualizar = useMutation({ mutationFn: (n: Nivel) => inv.update('ubicacion_niveles', n.id, { nombre: n.nombre, abreviatura: n.abreviatura || null, orden: n.orden }), onSuccess: () => void refetch(), onError: (e) => toast.error(getErrorMessage(e)) });
  const eliminar = useMutation({ mutationFn: (id: string) => inv.remove('ubicacion_niveles', id), onSuccess: () => void refetch(), onError: () => toast.error('No se puede eliminar: hay ubicaciones que usan este nivel') });
  const seed = useMutation({
    mutationFn: async () => { for (let i = 0; i < NIVELES_SUGERIDOS.length; i++) await inv.create('ubicacion_niveles', { ...NIVELES_SUGERIDOS[i], orden: i + 1 }); },
    onSuccess: () => { toast.success('Esquema sugerido cargado'); void refetch(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <>
      {dlg.ui}
      <Card>
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base">Niveles ({niveles.length})</CardTitle>
        {niveles.length === 0 && <Button size="sm" variant="outline" isLoading={seed.isPending} onClick={() => seed.mutate()}>Cargar esquema sugerido (Zona/Pasillo/Percha/Nivel/Fila)</Button>}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-2">
          <Campo label="Nivel" className="flex-1"><input className={fieldCls} placeholder="Percha, Nivel, Fila…" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter' && form.nombre.trim()) agregar.mutate(); }} /></Campo>
          <Campo label="Abrev." className="w-24"><input className={fieldCls} placeholder="PE" value={form.abreviatura} onChange={(e) => setForm({ ...form, abreviatura: e.target.value })} /></Campo>
          <Button size="sm" isLoading={agregar.isPending} disabled={!form.nombre.trim()} onClick={() => agregar.mutate()}><Plus className="w-4 h-4" /></Button>
        </div>
        {isFetching && <div className="py-2 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-2 w-16">Orden</th><th className="py-2 pr-2">Nombre</th><th className="py-2 pr-2 w-28">Abrev.</th><th className="py-2"></th></tr></thead>
          <tbody>
            {niveles.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Define el esquema (de mayor a menor: zona → posición)</td></tr>}
            {niveles.map((n) => (
              <tr key={n.id} className="border-b last:border-0">
                <td className="py-1 pr-2"><input type="number" className={`${fieldCls} h-8 w-16`} defaultValue={n.orden} onBlur={(e) => { const v = Number(e.target.value); if (v !== n.orden) actualizar.mutate({ ...n, orden: v }); }} /></td>
                <td className="py-1 pr-2"><input className={`${fieldCls} h-8`} defaultValue={n.nombre} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== n.nombre) actualizar.mutate({ ...n, nombre: e.target.value }); }} /></td>
                <td className="py-1 pr-2"><input className={`${fieldCls} h-8`} defaultValue={n.abreviatura ?? ''} onBlur={(e) => { if ((e.target.value || null) !== (n.abreviatura ?? null)) actualizar.mutate({ ...n, abreviatura: e.target.value }); }} /></td>
                <td className="py-1 text-right"><button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar nivel "${n.nombre}"?`, { peligro: true })) eliminar.mutate(n.id); }}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      </Card>
    </>
  );
}

// ── Ubicaciones (bins) por bodega ──────────────────────────────────────────────
function TabBins({ qc }: { qc: ReturnType<typeof useQueryClient> }) {
  const dlg = useDialogoEnLinea();
  const [bodega, setBodega] = useState('');
  const [tipo, setTipo] = useState('almacenamiento');
  const [gen, setGen] = useState<Record<string, { modo: 'rango' | 'lista'; texto: string }>>({});

  const { data: bodegas = [] } = useQuery<Bodega[]>({ queryKey: ['inv-bodegas-ubic'], queryFn: () => inv.list<Bodega>('locations', '?select=id,nombre,codigo&deleted_at=is.null&order=nombre') });
  const { data: niveles = [] } = useQuery<Nivel[]>({ queryKey: ['ubic-niveles'], queryFn: () => inv.list<Nivel>('ubicacion_niveles', '?order=orden') });
  const { data: bins = [], isFetching } = useQuery<Bin[]>({ queryKey: ['ubic-bins', bodega], queryFn: () => (bodega ? inv.list<Bin>('ubicaciones', `?bodega_id=eq.${bodega}&order=codigo`) : Promise.resolve([])), enabled: !!bodega });
  const refetch = () => qc.invalidateQueries({ queryKey: ['ubic-bins', bodega] });

  const valoresDe = (nid: string) => { const g = gen[nid]; return g ? expandir(g.modo, g.texto) : []; };
  const incluidos = niveles.filter((n) => valoresDe(n.id).length > 0);
  const combos = useMemo(() => (incluidos.length
    ? cartesian(incluidos.map((n) => valoresDe(n.id).map((v) => ({ nivel_id: n.id, valor: v }))))
    : []), [incluidos, gen]); // eslint-disable-line react-hooks/exhaustive-deps

  const generar = useMutation({
    mutationFn: () => invRpc<{ creadas: number; omitidas: number }>('ubicaciones_generar', { _bodega_id: bodega, _combos: combos, _tipo: tipo }),
    onSuccess: (r) => { toast.success(`${r.creadas} ubicación(es) creada(s)${r.omitidas ? ` · ${r.omitidas} ya existían` : ''}`); setGen({}); void refetch(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({ mutationFn: (id: string) => inv.remove('ubicaciones', id), onSuccess: () => { toast.success('Ubicación eliminada'); void refetch(); }, onError: () => toast.error('No se puede eliminar: la ubicación tiene stock o movimientos') });

  return (
    <div className="space-y-4">
      {dlg.ui}
      <Card>
        <CardContent className="pt-4 flex flex-wrap items-end gap-3">
          <Campo label="Bodega" className="min-w-64">
            <select className={fieldCls} value={bodega} onChange={(e) => setBodega(e.target.value)}>
              <option value="">— Selecciona una bodega —</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
        </CardContent>
      </Card>

      {!bodega ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Elige una bodega para ver y generar sus ubicaciones.</CardContent></Card>
      ) : niveles.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Primero define el <b>esquema de niveles</b> (pestaña de arriba).</CardContent></Card>
      ) : (
        <>
          {/* Generador */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Generar ubicaciones</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {niveles.map((n) => {
                  const g = gen[n.id] || { modo: 'rango' as const, texto: '' };
                  const cnt = valoresDe(n.id).length;
                  return (
                    <div key={n.id} className="rounded-md border p-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold">{n.nombre}</span>
                        <select className="h-7 rounded border border-input bg-background px-1 text-xs" value={g.modo}
                          onChange={(e) => setGen((s) => ({ ...s, [n.id]: { ...g, modo: e.target.value as 'rango' | 'lista' } }))}>
                          <option value="rango">Rango</option><option value="lista">Lista</option>
                        </select>
                      </div>
                      <input className={`${fieldCls} h-8`} placeholder={g.modo === 'rango' ? 'A-D · 1-10 · 01-20' : 'A, B, C'} value={g.texto}
                        onChange={(e) => setGen((s) => ({ ...s, [n.id]: { ...g, texto: e.target.value } }))} />
                      <p className="text-[11px] text-muted-foreground">{cnt > 0 ? `${cnt}: ${valoresDe(n.id).slice(0, 8).join(', ')}${cnt > 8 ? '…' : ''}` : 'vacío = no aplica'}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-end gap-2">
                  <Campo label="Tipo"><select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>{TIPOS_BIN.map((t) => <option key={t} value={t}>{t}</option>)}</select></Campo>
                  <span className="text-xs text-muted-foreground pb-2">{combos.length > 0 ? `Se generarán ${combos.length} ubicación(es)` : 'Ingresa valores en al menos un nivel'}</span>
                </div>
                <Button size="sm" isLoading={generar.isPending} disabled={combos.length === 0} onClick={() => generar.mutate()}>
                  <Boxes className="w-4 h-4 mr-1" /> Generar {combos.length > 0 ? `(${combos.length})` : ''}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Listado de bins */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Ubicaciones ({bins.length})</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              {isFetching && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Descripción</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th></tr></thead>
                <tbody>
                  {bins.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin ubicaciones — genera arriba</td></tr>}
                  {bins.map((b) => (
                    <tr key={b.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-1.5 pr-3 font-mono text-xs">{b.codigo}{b.es_default && <span className="ml-2 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px]">default</span>}</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">{b.nombre || '—'}</td>
                      <td className="py-1.5 pr-3">{b.tipo}</td>
                      <td className="py-1.5 pr-3">{b.estado}</td>
                      <td className="py-1.5 text-right">{!b.es_default && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar ubicación ${b.codigo}?`, { peligro: true })) eliminar.mutate(b.id); }}><Trash2 className="w-4 h-4" /></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
